import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ShieldCheck, ShieldWarning, QrCode } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { API } from '@/App';

/**
 * Admin 2FA enrollment + management.
 * Renders inside the Admin Settings tab.
 *
 * States:
 *   - "loading": initial status fetch
 *   - "off":    no 2FA → show "Enable 2FA" button
 *   - "setup":  pending setup → show QR + secret + code input
 *   - "on":     enabled → show "Disable 2FA" with code input
 */
const Admin2FAPanel = () => {
  const [state, setState] = useState('loading');
  const [enabledAt, setEnabledAt] = useState(null);
  const [setupData, setSetupData] = useState(null); // {secret, otpauthUri, qrPng}
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [backupCodes, setBackupCodes] = useState(null);  // {codes: [...], note} shown ONCE after enable / regen
  const [backupRemaining, setBackupRemaining] = useState(null);

  const token = localStorage.getItem('lottery_token');
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  const refresh = async () => {
    try {
      const r = await fetch(`${API}/admin/2fa/status`, { headers: authHeaders });
      if (!r.ok) {
        setState('off');
        return;
      }
      const d = await r.json();
      setEnabledAt(d.enabledAt || null);
      setState(d.enabled ? 'on' : 'off');
      if (d.enabled) {
        // Pull backup-code remaining count
        try {
          const br = await fetch(`${API}/admin/2fa/backup-codes/status`, { headers: authHeaders });
          if (br.ok) {
            const bd = await br.json();
            setBackupRemaining(bd.remainingCount ?? 0);
          }
        } catch {
          // non-fatal
        }
      }
    } catch (err) {
      console.warn('2FA status load failed', err);
      setState('off');
    }
  };

  useEffect(() => { refresh(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startSetup = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/admin/2fa/setup`, { method: 'POST', headers: authHeaders });
      if (!r.ok) throw new Error('Setup failed');
      const d = await r.json();
      setSetupData(d);
      setState('setup');
    } catch (err) {
      toast.error('Could not start 2FA setup');
    } finally {
      setBusy(false);
    }
  };

  const enable = async () => {
    if (code.length !== 6) {
      toast.error('Enter the 6-digit code from your authenticator app');
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(`${API}/admin/2fa/enable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ code }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || 'Invalid code');
      }
      const d = await r.json();
      toast.success('2FA enabled successfully — save your backup codes!');
      if (d.backupCodes && d.backupCodes.length) {
        setBackupCodes({ codes: d.backupCodes, note: d.backupCodesNote });
      }
      setSetupData(null);
      setCode('');
      refresh();
    } catch (err) {
      toast.error(err.message || 'Failed to enable 2FA');
    } finally {
      setBusy(false);
    }
  };

  const regenerateBackupCodes = async () => {
    if (code.length !== 6) {
      toast.error('Enter your current 6-digit code to regenerate backup codes');
      return;
    }
    if (!window.confirm(
      'Regenerate backup codes? Your existing 8 codes will be INVALIDATED immediately. ' +
      'You must save the new ones in a safe place.'
    )) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/admin/2fa/backup-codes/regenerate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ code }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || 'Invalid code');
      }
      const d = await r.json();
      setBackupCodes({ codes: d.backupCodes, note: d.backupCodesNote });
      setCode('');
      toast.success('New backup codes generated — save them now!');
      refresh();
    } catch (err) {
      toast.error(err.message || 'Failed to regenerate backup codes');
    } finally {
      setBusy(false);
    }
  };

  const copyBackupCodes = async () => {
    if (!backupCodes?.codes) return;
    try {
      await navigator.clipboard.writeText(backupCodes.codes.join('\n'));
      toast.success('Backup codes copied to clipboard');
    } catch {
      toast.error('Copy failed — please write them down manually');
    }
  };

  const downloadBackupCodes = () => {
    if (!backupCodes?.codes) return;
    const text = `1in2000club.com — Admin 2FA Backup Codes
Generated: ${new Date().toISOString()}

KEEP THESE SAFE. Each code works ONLY ONCE if you lose access to your authenticator app.

${backupCodes.codes.map((c, i) => `${i + 1}. ${c}`).join('\n')}

Store in a password manager and/or print + lock in a safe.
DO NOT share them with anyone. DO NOT email them.
`;
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `1in2000club-2fa-backup-codes-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const disable = async () => {
    if (code.length !== 6) {
      toast.error('Enter your current 6-digit code to disable 2FA');
      return;
    }
    if (!window.confirm('Disable 2FA? You will only need email + password to log in.')) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/admin/2fa/disable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ code }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || 'Invalid code');
      }
      toast.success('2FA disabled');
      setCode('');
      refresh();
    } catch (err) {
      toast.error(err.message || 'Failed to disable 2FA');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-[#1a1508]/60 border border-[#cba135]/20 rounded-lg p-5 sm:p-6" data-testid="admin-2fa-panel">
      <div className="flex items-center gap-3 mb-2">
        {state === 'on' ? (
          <ShieldCheck size={22} weight="duotone" className="text-[#e6be4b]" />
        ) : (
          <ShieldWarning size={22} weight="duotone" className="text-[#cba135]/60" />
        )}
        <h3 className="heading-font text-lg text-[#e6be4b]">Two-Factor Authentication</h3>
        {state === 'on' && (
          <span className="ml-auto text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#cba135]/15 text-[#e6be4b]">
            Active
          </span>
        )}
      </div>

      {state === 'loading' && (
        <p className="text-sm text-[#cba135]/50">Loading…</p>
      )}

      {state === 'off' && (
        <>
          <p className="text-sm text-[#cba135]/75 mb-4">
            Protect the admin panel with a 6-digit code from Google Authenticator, Authy, or 1Password.
            Adds a second layer of security on top of email + password.
          </p>
          <Button
            onClick={startSetup}
            disabled={busy}
            data-testid="2fa-enable-button"
            className="bg-gradient-to-r from-[#cba135] to-[#a8872b] text-[#0e0b04] font-semibold"
          >
            {busy ? 'Loading…' : 'Enable 2FA'}
          </Button>
        </>
      )}

      {state === 'setup' && setupData && (
        <div className="space-y-4">
          <p className="text-sm text-[#cba135]/75">
            <strong className="text-[#e6be4b]">Step 1:</strong> Scan this QR with your authenticator app
            (Google Authenticator / Authy / 1Password):
          </p>
          <div className="flex flex-col sm:flex-row items-start gap-5">
            <img
              src={setupData.qrPng}
              alt="2FA QR Code"
              className="h-44 w-44 bg-white p-2 rounded shrink-0"
              data-testid="2fa-qr-image"
            />
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-wider text-[#cba135]/55 mb-1">
                Or enter this key manually:
              </p>
              <code className="block text-xs sm:text-sm font-mono break-all bg-[#0e0b04] border border-[#cba135]/25 rounded px-3 py-2 text-[#cba135] mb-4">
                {setupData.secret}
              </code>
              <p className="text-sm text-[#cba135]/75 mb-2">
                <strong className="text-[#e6be4b]">Step 2:</strong> Enter the 6-digit code shown in your app:
              </p>
              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="bg-[#0e0b04] border-[#cba135]/30 text-[#cba135] tracking-[8px] text-center font-mono text-lg"
                data-testid="2fa-code-input"
              />
              <div className="flex gap-2 mt-3">
                <Button
                  onClick={enable}
                  disabled={busy || code.length !== 6}
                  data-testid="2fa-confirm-button"
                  className="bg-gradient-to-r from-[#cba135] to-[#a8872b] text-[#0e0b04] font-semibold"
                >
                  {busy ? 'Verifying…' : 'Confirm & Enable'}
                </Button>
                <Button
                  onClick={() => { setSetupData(null); setCode(''); setState('off'); }}
                  variant="outline"
                  className="border-[#cba135]/30 text-[#cba135]"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {state === 'on' && (
        <div className="space-y-4">
          <p className="text-sm text-[#cba135]/75">
            2FA is active{enabledAt ? ` since ${new Date(enabledAt).toLocaleDateString()}` : ''}.
            You&apos;ll be asked for a 6-digit code (or one backup code) on every login.
          </p>

          {/* Backup codes status */}
          <div className="rounded-lg border border-[#cba135]/15 bg-[#0e0b04]/40 p-3" data-testid="2fa-backup-status">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-widest text-[#a8872b]">Backup codes</p>
                <p className="text-sm text-[#cba135] mt-0.5">
                  {backupRemaining === null ? '…' : (
                    backupRemaining === 0 ? (
                      <span className="text-red-300">
                        No backup codes left — regenerate now to avoid lockout!
                      </span>
                    ) : (
                      <span>{backupRemaining} of 8 remaining</span>
                    )
                  )}
                </p>
              </div>
              <div className="text-[11px] text-[#cba135]/55 max-w-[300px]">
                Use a backup code (format XXXX-XXXX) instead of TOTP if you lose your authenticator.
                Each works only once.
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
            <Input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="bg-[#0e0b04] border-[#cba135]/30 text-[#cba135] tracking-[6px] text-center font-mono max-w-[180px]"
              data-testid="2fa-disable-code-input"
            />
            <Button
              onClick={regenerateBackupCodes}
              disabled={busy || code.length !== 6}
              variant="outline"
              data-testid="2fa-regen-backup-button"
              className="border-[#cba135]/40 text-[#e6be4b] hover:bg-[#cba135]/10"
            >
              {busy ? 'Working…' : 'Regenerate backup codes'}
            </Button>
            <Button
              onClick={disable}
              disabled={busy || code.length !== 6}
              variant="outline"
              data-testid="2fa-disable-button"
              className="border-red-500/40 text-red-300 hover:bg-red-500/10"
            >
              {busy ? 'Disabling…' : 'Disable 2FA'}
            </Button>
          </div>
        </div>
      )}

      {/* Backup codes reveal modal — shown ONCE after enable or regen */}
      {backupCodes?.codes && (
        <div
          className="fixed inset-0 z-[300] bg-[#0e0b04]/95 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) { /* prevent accidental close */ } }}
          data-testid="2fa-backup-codes-modal"
        >
          <div className="bg-[#1a1508] border border-[#e6be4b]/40 rounded-xl shadow-[0_20px_60px_rgba(0,0,0,0.9)] w-full max-w-md p-6 space-y-4">
            <div className="flex items-center gap-3">
              <ShieldCheck size={28} weight="duotone" className="text-[#e6be4b]" />
              <h3 className="heading-font text-xl text-[#e6be4b]">Save your backup codes</h3>
            </div>
            <p className="text-sm text-red-300/90">
              ⚠ Shown only ONCE. If you lose access to your authenticator, these codes are the only way to log in.
            </p>
            <div className="bg-[#0e0b04] border border-[#cba135]/30 rounded p-4 font-mono text-sm grid grid-cols-2 gap-2 tabular-nums" data-testid="2fa-backup-codes-list">
              {backupCodes.codes.map((c, i) => (
                <div key={c} className="text-[#e6be4b]">
                  <span className="text-[#cba135]/40 mr-2">{i + 1}.</span>{c}
                </div>
              ))}
            </div>
            <p className="text-xs text-[#cba135]/65 leading-relaxed">
              {backupCodes.note}
            </p>
            <div className="flex gap-2 flex-wrap">
              <Button
                onClick={copyBackupCodes}
                variant="outline"
                data-testid="2fa-backup-copy"
                className="border-[#cba135]/30 text-[#cba135] hover:bg-[#cba135]/10"
              >
                Copy all
              </Button>
              <Button
                onClick={downloadBackupCodes}
                variant="outline"
                data-testid="2fa-backup-download"
                className="border-[#cba135]/30 text-[#cba135] hover:bg-[#cba135]/10"
              >
                Download as .txt
              </Button>
              <Button
                onClick={() => setBackupCodes(null)}
                data-testid="2fa-backup-close"
                className="bg-gradient-to-r from-[#cba135] to-[#a8872b] text-[#0e0b04] font-semibold ml-auto"
              >
                I&apos;ve saved them
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin2FAPanel;
